import React, { useEffect, useMemo } from 'react';
import Wrapper from './styles';
import UiAnimate from '../../../ui/animate/UiAnimate';
import Svg from '../../../svg/Svg';
import ComboCardsBetslipStakeInput from '../stake-input/ComboCardsBetslipStakeInput';
import { loadOddsByMarketIds } from '../../../../microservices/sb-odds';
import { logger } from '../../../../services/logger';
import { useOddsByOutcomeIds } from '../../../../services/sports/hooks';
import { getMarketIdsFromComboCardMatches } from '../../../../services/sports/combo-cards';
import {
    subscribeToOddsChangesByMarketIds,
    unsubscribeFromOddsChangesByMarketIds,
} from '../../../../microservices/pusher';
import SportBetslipValidations from '../../../sport/betslip/validations/SportBetslipValidations';
import ComboCardsItemDetails from '../../item/details/ComboCardsItemDetails';
import { isInfo, isWarning } from '../../../../services/sports/betslip';
import UiAlert from '../../../ui/alert/UiAlert';
import { stores } from '../../../../stores';
import {
    COMBO_CARD_BETSLIP_MIN_MAX_STAKE,
    COMBO_CARD_NOT_AVAILABLE,
    COMBO_CARD_NOT_CASHOUT_ELIGIBLE,
    COMBO_MARKET_ID,
} from '../../../../services/sports/constants';
import { addBetslipError, removeBetslipError } from '../../../../services/sports/betslip-errors';
import { translate } from '../../../../services/translate';
import { FoComboCardWithOdds } from '@staycool/sports-types/fo-combo-card';
import { getActiveCurrency } from '../../../../services/currency';
import round from 'lodash/round';
import { DatabaseComboCardStakeSettings } from '@staycool/sports-types/dist/types';
import { useStore } from '../../../../hooks/useStore';
import { useCampaignOrNormalBetSlipFunctions } from '../../../../hooks/useCampaignOrNormalBetslipFunctions';

interface Props {
    card: FoComboCardWithOdds;
    clearSelections: (id?: number) => void;
}
export default function ComboCardsBetslipSelection({ card, clearSelections }: Props) {
    const { id, name, matches, disable_cashout: disableCashout, odds_boost } = card;
    const [oddsByOutcomeId] = useOddsByOutcomeIds(
        matches.flatMap(({ markets }) => markets.map(({ outcome_id }) => outcome_id)),
    );
    const [betSlipErrorByMarketId] = useStore(stores.sports.betSlipErrorByMarketId);
    const genericErrors = betSlipErrorByMarketId[String(COMBO_MARKET_ID)] || [];
    const { getErrorMessageByError } = useCampaignOrNormalBetSlipFunctions();
    const [stakeByCardId] = useStore(stores.sports.comboCard.stakeByCardId);
    const [ineligibleCardIds] = useStore(stores.sports.comboCard.ineligibleCardIds);
    const isComboCardNowIneligible = ineligibleCardIds.includes(id);

    useEffect(() => {
        validateComboCardStakeSettings(stakeByCardId[card.id], card.combo_card_stake_settings);
    }, [stakeByCardId]);

    function validateComboCardStakeSettings(currentStake: number, stakeSettings: DatabaseComboCardStakeSettings[]) {
        const currentStakeSetting = stakeSettings.find((stakeSetting) => stakeSetting.currency === getActiveCurrency());
        if (!currentStakeSetting) {
            return;
        }

        const { min_stake: minStake, max_stake: maxStake, currency } = currentStakeSetting;
        const stakeError = { code: COMBO_CARD_BETSLIP_MIN_MAX_STAKE, minStake, maxStake };
        removeBetslipError(COMBO_MARKET_ID, stakeError);
        if (minStake > currentStake || maxStake < currentStake || currency !== getActiveCurrency()) {
            addBetslipError(COMBO_MARKET_ID, stakeError);
        }
    }

    useEffect(() => {
        if (!matches.length) {
            return;
        }
        const marketIds = getMarketIdsFromComboCardMatches(matches);
        loadOddsByMarkets(marketIds);
        subscribeToOddsChangesByMarketIds(marketIds);
        return () => unsubscribeFromOddsChangesByMarketIds(marketIds);
    }, []);

    const totalOdds = useMemo(() => {
        const allOdds = matches
            .flatMap(({ markets }) => markets.map(({ outcome_id }) => oddsByOutcomeId[outcome_id]?.value))
            .filter((odds): odds is number => !!odds);
        return allOdds.reduce((a, b) => a * b, 1);
    }, [oddsByOutcomeId]);

    async function loadOddsByMarkets(marketIds: number[] = []) {
        try {
            await loadOddsByMarketIds(marketIds);
        } catch (error) {
            logger.error(
                'ComboCardsBetslipSelection',
                'loadOddsByMarkets',
                `Unable to load odds by marketIds for cardId ${id}`,
            );
        }
    }

    return (
        <UiAnimate animationIn="fadeIn" animationOut="fadeOut" isVisible={true} key={id}>
            <Wrapper>
                <div className="selection-header">
                    <div className="card-name">{name}</div>
                    <Svg
                        icon="trash"
                        size={1}
                        onClick={() => clearSelections(id)}
                        className="remove-selection pointer"
                    />
                </div>
                <ComboCardsItemDetails card={card} />
                <SportBetslipValidations />
                <ComboCardsBetslipStakeInput cardId={id} totalOdds={round(totalOdds * odds_boost, 3)} />
                {isComboCardNowIneligible && (
                    <UiAlert failure>
                        {translate([COMBO_CARD_NOT_AVAILABLE, 'Combo card is not available'], 'ui.betslip')}
                    </UiAlert>
                )}
                {disableCashout && (
                    <UiAlert warning>{translate(COMBO_CARD_NOT_CASHOUT_ELIGIBLE, 'ui.betslip')}</UiAlert>
                )}
                {genericErrors.map((genericError) => (
                    <UiAlert
                        className="market-error"
                        info={isInfo(genericError)}
                        warning={isWarning(genericError)}
                        failure={!isWarning(genericError) && !isInfo(genericError)}
                        key={genericError}
                    >
                        {getErrorMessageByError(genericError)}
                    </UiAlert>
                ))}
            </Wrapper>
        </UiAnimate>
    );
}
